import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WifiHigh, Plus, Trash, QrCode, Clock, Copy, Check, XCircle } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

export default function WiFiPage() {
  const { api } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ssid: '', password: '', guest_name: '', expires_hours: 24, notes: '' });
  const [loading, setLoading] = useState(true);
  const [qrUrl, setQrUrl] = useState(null);
  const [copied, setCopied] = useState('');

  const fetchData = async () => {
    try {
      const res = await api.get('/wifi/credentials');
      setCredentials(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/wifi/credentials', { ...form, expires_hours: Number(form.expires_hours) });
      setShowForm(false);
      setForm({ ssid: '', password: '', guest_name: '', expires_hours: 24, notes: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/wifi/credentials/${id}`); fetchData(); } catch (e) { console.error(e); }
  };

  const handleDeactivate = async (id) => {
    try { await api.post(`/wifi/credentials/${id}/deactivate`); fetchData(); } catch (e) { console.error(e); }
  };

  const showQR = (id) => {
    const baseUrl = apiOrigin();
    const token = localStorage.getItem('hc_token');
    setQrUrl(`${baseUrl}/api/wifi/credentials/${id}/qr?token=${token}`);
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghkmnpqrstuvwxyz23456789';
    let pw = '';
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setForm({ ...form, password: pw });
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="wifi-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Network</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">Guest Wi-Fi</h1>
          <p className="text-sm text-neutral-500 mt-1">Create temporary credentials for guests</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="create-wifi-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> Create Credentials
        </button>
      </div>

      {/* Credentials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {credentials.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <WifiHigh size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No credentials created yet</p>
          </div>
        ) : credentials.map(cred => {
          const isExpired = !cred.active || new Date(cred.expires_at) < new Date();
          return (
            <div key={cred.wifi_id} className={`glass-card p-6 ${isExpired ? 'opacity-50' : ''}`} data-testid={`wifi-card-${cred.wifi_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${isExpired ? 'bg-neutral-800' : 'bg-[#00E5FF]/10'}`}>
                    <WifiHigh size={20} className={isExpired ? 'text-neutral-600' : 'text-[#00E5FF]'} weight="duotone" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{cred.ssid}</p>
                    {cred.guest_name && <p className="text-[11px] text-neutral-500">For: {cred.guest_name}</p>}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${isExpired ? 'bg-red-500/10 text-red-400' : 'bg-[#22C55E]/10 text-[#22C55E]'}`}>
                  {isExpired ? 'Expired' : 'Active'}
                </span>
              </div>

              {/* Password */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-neutral-500 font-mono bg-white/5 px-3 py-1.5 rounded-lg flex-1">{cred.password}</span>
                <button onClick={() => copyToClipboard(cred.password, cred.wifi_id)} className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-white transition-colors">
                  {copied === cred.wifi_id ? <Check size={14} className="text-[#22C55E]" /> : <Copy size={14} />}
                </button>
              </div>

              {/* Expiry */}
              <div className="flex items-center gap-2 text-[11px] text-neutral-500 mb-4">
                <Clock size={12} />
                <span>Expires: {new Date(cred.expires_at).toLocaleString()}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {!isExpired && (
                  <>
                    <button onClick={() => showQR(cred.wifi_id)} data-testid={`qr-btn-${cred.wifi_id}`}
                      className="flex-1 py-2 rounded-full border border-white/10 text-xs font-medium text-neutral-300 hover:bg-white/5 flex items-center justify-center gap-1">
                      <QrCode size={14} /> QR Code
                    </button>
                    <button onClick={() => handleDeactivate(cred.wifi_id)}
                      className="flex-1 py-2 rounded-full border border-white/10 text-xs font-medium text-neutral-300 hover:bg-white/5 flex items-center justify-center gap-1">
                      <XCircle size={14} /> Revoke
                    </button>
                  </>
                )}
                <button onClick={() => handleDelete(cred.wifi_id)}
                  className="py-2 px-3 rounded-full border border-white/10 text-xs text-neutral-500 hover:text-red-400 hover:border-red-400/20 transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* QR Modal */}
      {qrUrl && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setQrUrl(null)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-8 text-center" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-4">Scan to Connect</h3>
            <div className="bg-white rounded-2xl p-4 inline-block">
              <img src={qrUrl} alt="WiFi QR Code" className="w-48 h-48" />
            </div>
            <p className="text-xs text-neutral-500 mt-4">Guest scans this to auto-connect</p>
            <button onClick={() => setQrUrl(null)} className="mt-4 px-6 py-2 rounded-full border border-white/10 text-sm text-neutral-400 hover:bg-white/5">Close</button>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">Create Guest Wi-Fi</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="overline block mb-2">Network Name (SSID)</label>
                <input type="text" value={form.ssid} onChange={e => setForm({ ...form, ssid: e.target.value })} required
                  data-testid="wifi-ssid-input" placeholder="e.g., HomeGuest"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">Password</label>
                <div className="flex gap-2">
                  <input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required
                    data-testid="wifi-password-input"
                    className="flex-1 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50 font-mono" />
                  <button type="button" onClick={generatePassword}
                    className="px-4 py-3 rounded-2xl border border-white/10 text-xs text-neutral-400 hover:bg-white/5 whitespace-nowrap">Auto</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Guest Name</label>
                  <input type="text" value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })}
                    data-testid="wifi-guest-input" placeholder="Optional"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Expires in (hours)</label>
                  <input type="number" value={form.expires_hours} onChange={e => setForm({ ...form, expires_hours: e.target.value })}
                    data-testid="wifi-expires-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">Cancel</button>
                <button type="submit" data-testid="save-wifi-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
